import * as React from 'react';
import { useEffect, useState } from 'react';
import { ApiFieldModel, ApiField, getFieldModels, addFieldModel, updateFieldModel, deleteFieldModel, getFieldsByModel, addField, updateField, deleteField } from '@api/index';
import { listLookups, getLookup } from '@api/index';
import type { LookupFieldMapping } from '@api/index';
import OptionsEditor from './OptionsEditor'; // Editor for dropdown options

interface FieldModelForm { fieldModelName: string; fieldModelDescription: string; fieldModelType: string; }
interface FieldForm { fieldName: string; fieldDataType: string; fieldLength: number; fieldRequired: boolean; fieldTitle: string; fieldOffset: string; fieldShowInSearch: boolean; fieldShowInDataGrid: boolean; fieldGroup: string; fieldIsUniqueFolderIndex: boolean; fieldIsUniqueDocumentIndex: boolean; fieldControlOrderBy: number; fieldUseAsSortingVar: boolean; options: string[]; fieldLookupName: string; fieldLookupKeyColumn: string; fieldLookupDisplayColumn: string; fieldLookupMappings: LookupFieldMapping[]; }

const emptyModel: FieldModelForm = { fieldModelName: '', fieldModelDescription: '', fieldModelType: '' };
const emptyField: FieldForm = { fieldName: '', fieldDataType: 'X', fieldLength: 0, fieldRequired: false, fieldTitle: '', fieldOffset: '', fieldShowInSearch: false, fieldShowInDataGrid: false, fieldGroup: '', fieldIsUniqueFolderIndex: false, fieldIsUniqueDocumentIndex: false, fieldControlOrderBy: 0, fieldUseAsSortingVar: true, options: [], fieldLookupName: '', fieldLookupKeyColumn: '', fieldLookupDisplayColumn: '', fieldLookupMappings: [] };

// Restrict datatype options to the four supported values and map to backend codes
const dataTypeOptions = [
  { value: 'text', label: 'Text', code: 'X' },
  { value: 'decimal', label: 'Decimal', code: 'D' },
  { value: 'currency', label: 'Currency', code: 'V' },
  { value: 'datetime', label: 'Date/Time', code: 'T' }
];

// Model type options
const modelTypeOptions = [
  { value: 'Document', label: 'Document' },
  { value: 'Folder', label: 'Folder' }
];

export default function FieldDefinitionsAdmin() {
  const [models, setModels] = useState<ApiFieldModel[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [modelEditing, setModelEditing] = useState(false);
  const [modelForm, setModelForm] = useState<FieldModelForm>(emptyModel);
  const [fields, setFields] = useState<ApiField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [fieldEditing, setFieldEditing] = useState(false);
  const [fieldForm, setFieldForm] = useState<FieldForm>(emptyField);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableLookups, setAvailableLookups] = useState<string[]>([]);
  const [lookupColumns, setLookupColumns] = useState<string[]>([]);
  const [loadingLookupCols, setLoadingLookupCols] = useState(false);

  function normalizeFieldName(input: string): string {
    const raw = (input ?? '').toString().toLowerCase();
    // Transliterate accented characters to ASCII (e.g. é -> e) and drop combining marks
    const s = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Replace non-alphanumeric with underscores
    let n = s.replace(/[^a-z0-9]+/g, '_');
    // Trim underscores
    n = n.replace(/^_+|_+$/g, '');
    return n;
  }

  function setFieldNameNormalized(raw: string) {
    const normalized = normalizeFieldName(raw);
    setFieldForm(p => ({ ...p, fieldName: normalized }));
  }

  function setFieldTitleWithAutofill(raw: string) {
    setFieldForm(p => {
      const nextTitle = raw;
      const nextName = (p.fieldName ?? '').trim();

      // Autofill name if name is empty OR equals the normalized previous title (user hasn't customized it)
      const prevTitleNorm = normalizeFieldName(p.fieldTitle ?? '');
      const shouldAutofill = !nextName || nextName === prevTitleNorm;
      return {
        ...p,
        fieldTitle: nextTitle,
        fieldName: shouldAutofill ? normalizeFieldName(nextTitle) : p.fieldName
      };
    });
  }

  // Field name validation: only letters, digits and underscore; no spaces or special characters.
  const fieldNameRegex = /^[A-Za-z0-9_]+$/;

  useEffect(() => { loadModels(); listLookups().then(setAvailableLookups).catch(() => setAvailableLookups([])); }, []);

  // Load column names for the selected lookup
  useEffect(() => {
    if (!fieldForm.fieldLookupName) { setLookupColumns([]); return; }
    let cancelled = false;
    setLoadingLookupCols(true);
    getLookup(fieldForm.fieldLookupName)
      .then(result => {
        if (cancelled) return;
        const items = Array.isArray(result?.data) ? result.data : [];
        setLookupColumns(items.length > 0 ? Object.keys(items[0]) : []);
      })
      .catch(() => { if (!cancelled) setLookupColumns([]); })
      .finally(() => { if (!cancelled) setLoadingLookupCols(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldForm.fieldLookupName]);
  // Keep the edit form in sync with the selected field after grid reloads.
  useEffect(() => {
    if (selectedFieldId != null) {
      loadSelectedFieldIntoForm(selectedFieldId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, selectedFieldId]);
  async function loadModels() { setLoading(true); setError(null); try { setModels(await getFieldModels()); } catch(e:any){ setError(e.message);} finally { setLoading(false);} }
  async function loadFields(modelId:number){ try { setFields(await getFieldsByModel(modelId)); } catch { setFields([]);} }

  function handleSelectModel(id:number){ setSelectedModelId(id); setModelEditing(false); setSelectedFieldId(null); setFieldEditing(false); loadFields(id); const m=models.find(x=>x.id===id); if(m) setModelForm({ fieldModelName:m.fieldModelName ?? '', fieldModelDescription:m.fieldModelDescription ?? '', fieldModelType:m.fieldModelType ?? '' }); }
  function handleAddModel(){ setSelectedModelId(null); setModelEditing(true); setModelForm(emptyModel); setFields([]); setSelectedFieldId(null); }
  function handleEditModel(){ if(selectedModelId!=null) setModelEditing(true); }
  async function handleDeleteModel(){ if(selectedModelId==null) return; if(!window.confirm('Delete this field model (and its fields)?')) return; setLoading(true); setError(null); try { await deleteFieldModel(selectedModelId); await loadModels(); setSelectedModelId(null); setFields([]);} catch(e:any){ setError(e.message);} finally { setLoading(false);} }
  async function handleSaveModel(){ setLoading(true); setError(null); try { const payload={ fieldModelName:modelForm.fieldModelName, fieldModelDescription:modelForm.fieldModelDescription, fieldModelType:modelForm.fieldModelType } as Omit<ApiFieldModel,'id'>; let id=selectedModelId; if(id==null){ const nm=await addFieldModel(payload); id=nm.id; await loadModels(); } else { await updateFieldModel(id,payload); await loadModels(); } setSelectedModelId(id!); setModelEditing(false); await loadFields(id!);} catch(e:any){ setError(e.message);} finally { setLoading(false);} }

    // Map datatype display name (from backend) to code used in form
    function dataTypeNameToCode(name?: string): string {
        const n = name?.toLowerCase();
        if (!n) return emptyField.fieldDataType;
        if (n === 'text') return 'X';
        if (n === 'decimal' || n === 'number' || n === 'numeric') return 'D';
        if (n === 'currency' || n === 'valuta') return 'V';
        if (n === 'datetime' || n === 'date' || n === 'date/time') return 'T';
        return emptyField.fieldDataType;
    }

  function loadSelectedFieldIntoForm(id: number) {
    const f = fields.find(x => x.id === id);
    if (!f) return;
    const dt = (f.fieldDataType && String(f.fieldDataType).trim()) || dataTypeNameToCode((f as any).fieldDatatypeName);

    const rawUse = (f as any).fieldUseAsSortingVar ?? (f as any).FieldUseAsSortingVar;
    const useAsSorting = rawUse == null ? true : String(rawUse).toLowerCase() !== 'false';

    const rawMappings = (f as any).fieldLookupMappings ?? (f as any).FieldLookupMappings;
    const mappings: LookupFieldMapping[] = Array.isArray(rawMappings)
      ? rawMappings.map((m: any) => ({ lookupColumn: m.lookupColumn ?? m.LookupColumn ?? '', targetFieldName: m.targetFieldName ?? m.TargetFieldName ?? '' }))
      : [];

    setFieldForm({
      fieldName: f.fieldName,
      fieldDataType: dt || emptyField.fieldDataType,
      fieldLength: f.fieldLength,
      fieldRequired: f.fieldRequired,
      fieldTitle: f.fieldTitle || '',
      fieldOffset: f.fieldOffset || '',
      fieldShowInSearch: !!f.fieldShowInSearch,
      fieldShowInDataGrid: !!f.fieldShowInDataGrid,
      fieldGroup: (f as any).fieldGroup ?? (f as any).FieldGroup ?? '',
      fieldIsUniqueFolderIndex: !!f.fieldIsUniqueFolderIndex,
      fieldIsUniqueDocumentIndex: !!f.fieldIsUniqueDocumentIndex,
      fieldControlOrderBy: Number((f as any).fieldControlOrderBy ?? 0),
      fieldUseAsSortingVar: useAsSorting,
      options: Array.isArray((f as any).options) ? ([...((f as any).options)] as string[]) : [],
      fieldLookupName: (f as any).fieldLookupName ?? (f as any).FieldLookupName ?? '',
      fieldLookupKeyColumn: (f as any).fieldLookupKeyColumn ?? (f as any).FieldLookupKeyColumn ?? '',
      fieldLookupDisplayColumn: (f as any).fieldLookupDisplayColumn ?? (f as any).FieldLookupDisplayColumn ?? '',
      fieldLookupMappings: mappings
    });
  }

  function handleSelectField(id:number){ setSelectedFieldId(id); setFieldEditing(true); loadSelectedFieldIntoForm(id); }
  function handleAddField(){
    const maxExisting = fields
      .map(f => Number((f as any)?.fieldControlOrderBy ?? 0))
      .filter(n => Number.isFinite(n) && n > 0)
      .reduce((max, n) => Math.max(max, n), 0);

    setSelectedFieldId(null);
    setFieldEditing(true);
    setFieldForm({ ...emptyField, fieldControlOrderBy: maxExisting + 1 });
  }
  function handleEditField(){ if(selectedFieldId!=null) { loadSelectedFieldIntoForm(selectedFieldId); setFieldEditing(true); } }

  function handleCancelFieldEdit() {
    if (selectedFieldId != null) {
      // revert any local edits back to the selected field's current values
      loadSelectedFieldIntoForm(selectedFieldId);
      setFieldEditing(false);
    } else {
      setFieldEditing(false);
    }
  }
  async function handleDeleteField(){ if(selectedFieldId==null) return; if(!window.confirm('Delete this field?')) return; setLoading(true); setError(null); try { await deleteField(selectedFieldId); await loadFields(selectedModelId!); setSelectedFieldId(null);} catch(e:any){ setError(e.message);} finally { setLoading(false);} }
  async function handleSaveField(){ 
    if(selectedModelId==null) return; 
    setLoading(true); 
    setError(null); 
    try { 
      // Use code directly as selected value for API
      const apiDataType = fieldForm.fieldDataType; 
      const payload={ 
        fieldModelId:selectedModelId, 
        fieldName:fieldForm.fieldName, 
        fieldDataType: apiDataType, 
        fieldLength:fieldForm.fieldLength, 
        fieldRequired:fieldForm.fieldRequired, 
        fieldTitle:fieldForm.fieldTitle, 
        fieldOffset:fieldForm.fieldOffset, 
        // legacy flags (kept for compatibility)
        fieldShowInSearch:fieldForm.fieldShowInSearch, 
        fieldShowInDataGrid:fieldForm.fieldShowInDataGrid, 
        fieldGroup: fieldForm.fieldGroup,
        fieldIsUniqueFolderIndex:fieldForm.fieldIsUniqueFolderIndex, 
        fieldIsUniqueDocumentIndex:fieldForm.fieldIsUniqueDocumentIndex,
        fieldControlOrderBy: fieldForm.fieldControlOrderBy,
        fieldUseAsSortingVar: fieldForm.fieldUseAsSortingVar ? 'true' : 'false',
        options: fieldForm.options,
        fieldLookupName: fieldForm.fieldLookupName || undefined,
        fieldLookupKeyColumn: fieldForm.fieldLookupKeyColumn || undefined,
        fieldLookupDisplayColumn: fieldForm.fieldLookupDisplayColumn || undefined,
        fieldLookupMappings: fieldForm.fieldLookupMappings.length > 0 ? fieldForm.fieldLookupMappings : undefined
      } as Omit<ApiField,'id'>;
      let createdId: number | null = null;
      if(selectedFieldId==null) {
        const created = await addField(payload);
        createdId = (created as any)?.id ?? null;
        setSelectedFieldId(createdId);
      } else {
        await updateField(selectedFieldId,payload);
      }
      await loadFields(selectedModelId);
      // Keep user in edit mode so they can continue editing.
      setFieldEditing(true);
    } catch(e:any){ 
      setError(e.message);
    } finally { 
      setLoading(false);
    } 
  }

  function onModelFormChange(e: React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>){ const {name,value}=e.target; setModelForm(p=>({...p,[name]:value})); }
  function onFieldFormChange(e: React.ChangeEvent<HTMLInputElement|HTMLSelectElement>){
    const tgt = e.target as HTMLInputElement;
    const { name, value, type, checked } = tgt;
    setFieldForm(p => ({
      ...p,
      // A single UI checkbox controls both unique flags.
      ...(name === 'fieldIsUnique'
        ? { fieldIsUniqueFolderIndex: checked, fieldIsUniqueDocumentIndex: checked }
        : { [name]: type === 'checkbox' ? checked : (name === 'fieldLength' || name === 'fieldControlOrderBy' ? Number(value) : value) })
    } as any));
  }


  // Helper function to get display label for datatype codes
  function getDataTypeLabel(code: string): string {
    const option = dataTypeOptions.find(opt => opt.code === code);
    return option?.label || code;
  }

  const selectedModel=models.find(m=>m.id===selectedModelId)||null;
  const selectedField=fields.find(f=>f.id===selectedFieldId)||null;

  const fieldNameTrimmed = (fieldForm.fieldName ?? '').trim();
  const hasFieldNameValue = fieldNameTrimmed.length > 0;
  const isFieldNameValid = !hasFieldNameValue || fieldNameRegex.test(fieldNameTrimmed);
  const fieldNameError = (!isFieldNameValid && hasFieldNameValue) ? "Only letters, digits and underscore are allowed." : null;

  // Sort models by name (then type, then id) for predictable UI ordering.
  const sortedModels = React.useMemo(() => {
    return [...models].sort((a, b) => {
      const byName = (a.fieldModelName ?? '').localeCompare((b.fieldModelName ?? ''), undefined, { sensitivity: 'base' });
      if (byName !== 0) return byName;
      const byType = (a.fieldModelType ?? '').localeCompare((b.fieldModelType ?? ''), undefined, { sensitivity: 'base' });
      if (byType !== 0) return byType;
      return (a.id ?? 0) - (b.id ?? 0);
    });
  }, [models]);

  // Sort fields by fieldControlOrderBy then by name
  const sortedFields = React.useMemo(() => {
    const getOrder = (f: any) => (typeof f.fieldControlOrderBy === 'number' ? f.fieldControlOrderBy : Number(f.fieldControlOrderBy ?? Number.MAX_SAFE_INTEGER));
    return [...fields].sort((a: any, b: any) => {
      const ao = getOrder(a);
      const bo = getOrder(b);
      if (ao !== bo) return (ao ?? Number.MAX_SAFE_INTEGER) - (bo ?? Number.MAX_SAFE_INTEGER);
      return (a.fieldName || '').localeCompare(b.fieldName || '');
    });
  }, [fields]);

  return (
      <div className="adm-shell adm-shell--tall">
      <div className="adm-master">
        <div className="adm-master-head">
          <h3>Field Models</h3>
          <button onClick={handleAddModel} title='Add field model' className="adm-add-btn">
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='12' y1='5' x2='12' y2='19'/>
              <line x1='5' y1='12' x2='19' y2='12'/>
            </svg>
          </button>
        </div>
        {loading && <div className="adm-status">Loading...</div>}
        {error && <div className="adm-status adm-status--error">{error}</div>}
        <div className="adm-master-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {sortedModels.map(m=>{
                const sel=m.id===selectedModelId;
                return (
                  <tr key={m.id} onClick={()=>handleSelectModel(m.id)} className={`adm-row${sel ? ' is-selected' : ''}`}>
                    <td>{m.fieldModelName}</td>
                    <td>{m.fieldModelType}</td>
                  </tr>
                );
              })}
              {sortedModels.length===0 && !loading && (
                <tr>
                  <td colSpan={2} className="adm-empty">No field models</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="adm-detail adm-detail--scroll">
        {modelEditing ? (
          <div style={{ maxWidth:900 }}>
            <h3 style={{ marginTop:0 }}>{selectedModelId==null? 'Add Field Model':'Edit Field Model'}</h3>
            <form onSubmit={e=>{ e.preventDefault(); handleSaveModel(); }} className="adm-card" style={{ display:'grid', gridTemplateColumns:'170px 1fr 170px 1fr', gap:12 }}>
              <label className="adm-label">Name</label>
              <input name='fieldModelName' value={modelForm.fieldModelName} onChange={onModelFormChange} required className="adm-input" />
              <label className="adm-label">Type</label>
              <select name='fieldModelType' value={modelForm.fieldModelType} onChange={onModelFormChange} required className="adm-input">
                <option value=''>Select type...</option>
                {modelTypeOptions.map(o=> <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <label className="adm-label">Description</label>
              <input name='fieldModelDescription' value={modelForm.fieldModelDescription} onChange={onModelFormChange} className="adm-input" style={{ gridColumn:'span 3' }} />
              <div style={{ gridColumn:'1 / span 4', display:'flex', gap:12, marginTop:4 }}>
                <button type='submit' className="adm-btn adm-btn-primary" disabled={loading}>Save</button>
                <button type='button' className="adm-btn adm-btn-secondary" onClick={()=>setModelEditing(false)} disabled={loading}>Cancel</button>
              </div>
            </form>
          </div>
        ) : selectedModel ? (
          <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
            <div style={{ maxWidth:1000 }}>
              <div className="adm-toolbar">
                <h3 style={{ margin:0 }}>Field Model Details</h3>
                <div className="adm-actions-group" style={{ gap:8 }}>
                  <button onClick={handleEditModel} className="adm-btn adm-btn-secondary" disabled={loading}>Edit</button>
                  <button onClick={handleDeleteModel} className="adm-btn adm-btn-danger" disabled={loading}>Delete</button>
                </div>
              </div>
              <div className="adm-card" style={{ display:'grid', gridTemplateColumns:'160px 1fr', rowGap:8 }}>
                <div className="adm-field-label">Name:</div>
                <div>{selectedModel.fieldModelName}</div>
                <div className="adm-field-label">Description:</div>
                <div>{selectedModel.fieldModelDescription}</div>
                <div className="adm-field-label">Type:</div>
                <div>{selectedModel.fieldModelType}</div>
              </div>
            </div>
            <div style={{ maxWidth:1400 }}>
              <div className="adm-toolbar">
                <h3 style={{ margin:0 }}>Fields</h3>
                <button onClick={handleAddField} className="adm-btn adm-btn-secondary" disabled={loading}>Add Field</button>
              </div>
              <div style={{ border:'1px solid var(--sc-border)', borderRadius:12, overflow:'hidden', background:'var(--sc-surface)', maxHeight:'400px' }}>
                <div style={{ overflowY:'auto', maxHeight:'400px' }}>
                  <table className="adm-table">
                    <thead>
                      <tr style={{ position:'sticky', top:0 }}>
                        <th>Order</th>
                        <th>Name</th>
                        <th>Title</th>
                        <th>Type</th>
                        <th>Length</th>
                        <th>Req</th>
                        <th>Group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFields.map((f:any)=>{
                        const sel=f.id===selectedFieldId;
                        const isUnique = !!f.fieldIsUniqueFolderIndex || !!f.fieldIsUniqueDocumentIndex;
                        return (
                          <tr key={f.id} onClick={()=>handleSelectField(f.id)} className={`adm-row${sel ? ' is-selected' : ''}`}>
                            <td>{f.fieldControlOrderBy}</td>
                            <td>
                              {f.fieldName}
                              {isUnique && <span title="Unique index" style={{ marginLeft: 6, color: '#b58800' }}>&#x1F511;</span>}
                            </td>
                            <td>{f.fieldTitle}</td>
                            <td>{f.fieldDatatypeName}</td>
                            <td>{f.fieldLength}</td>
                            <td>{f.fieldRequired? 'Yes':'No'}</td>
                            <td>{(f.fieldGroup ?? f.FieldGroup) || ''}</td>
                          </tr>
                        );
                      })}
                      {sortedFields.length===0 && (
                        <tr>
                          <td colSpan={7} className="adm-empty">No fields</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {(fieldEditing || selectedFieldId != null) ? (
                <form onSubmit={e=>{ e.preventDefault(); handleSaveField(); }} className="adm-card" style={{ marginTop:16, display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                  {/* Left column */}
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'150px 1fr', gap:12, alignItems:'start' }}>
                      <label style={{ paddingTop:6, fontWeight:500 }}>Title</label>
                      <input
                        name='fieldTitle'
                        value={fieldForm.fieldTitle}
                        onChange={e => setFieldTitleWithAutofill((e.target as HTMLInputElement).value)}
                        className="adm-input"
                      />

                      <label style={{ paddingTop:6, fontWeight:500 }}>Name</label>
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        <input
                          name='fieldName'
                          value={fieldForm.fieldName}
                          onChange={e => setFieldNameNormalized((e.target as HTMLInputElement).value)}
                          required
                          pattern="[A-Za-z0-9_]+"
                          title={fieldNameError ?? undefined}
                          aria-invalid={!!fieldNameError}
                          className="adm-input"
                          style={fieldNameError ? { borderColor: 'var(--sc-danger)' } : undefined}
                        />
                        {!!fieldNameError && (
                          <div style={{ color:'var(--sc-danger)', fontSize:12 }}>{fieldNameError}</div>
                        )}
                      </div>

                      <label style={{ paddingTop:6, fontWeight:500 }}>Group</label>
                      <input name='fieldGroup' value={fieldForm.fieldGroup} onChange={onFieldFormChange} className="adm-input" placeholder='Optional group header' />
                    </div>

                    {/* Dropdown options editor for text fields (left column) */}
                    {fieldForm.fieldDataType === 'X' && (
                      <div style={{ borderTop:'1px solid #eef2f6', paddingTop:12, marginTop:4 }}>
                        <div className="adm-section-title">Dropdown Options</div>
                        <div className="adm-hint" style={{ marginBottom:10 }}>
                          Only for validation input/edit dialogs. Does not affect existing values.
                        </div>
                        <OptionsEditor
                          value={fieldForm.options}
                          onChange={(opts)=>setFieldForm(p=>({ ...p, options: opts }))}
                        />
                      </div>
                    )}
                  </div>

                  {/* Right column */}
                  <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'150px 1fr', gap:12, alignItems:'center' }}>
                      <label style={{ fontWeight:500 }}>Data Type</label>
                      <select name='fieldDataType' value={fieldForm.fieldDataType} onChange={onFieldFormChange} className="adm-input" style={{ maxWidth: 190 }}>
                        {dataTypeOptions.map(o=> <option key={o.code} value={o.code}>{o.label}</option>)}
                      </select>

                      <label style={{ fontWeight:500 }}>Required</label>
                      <div style={{ display:'flex', alignItems:'center', height:'100%' }}>
                        <input type='checkbox' name='fieldRequired' checked={fieldForm.fieldRequired} onChange={onFieldFormChange} />
                      </div>

                      <label style={{ fontWeight:500 }}>Include in folder description</label>
                      <div style={{ display:'flex', alignItems:'center', height:'100%' }}>
                        <input
                          type='checkbox'
                          name='fieldUseAsSortingVar'
                          checked={!!fieldForm.fieldUseAsSortingVar}
                          onChange={onFieldFormChange}
                          title='When enabled, this field is included when computing the folder display/description.'
                        />
                      </div>

                      <label style={{ fontWeight:500 }}>Unique</label>
                      <div style={{ display:'flex', alignItems:'center', height:'100%' }}>
                        <input
                          type='checkbox'
                          name='fieldIsUnique'
                          checked={!!fieldForm.fieldIsUniqueFolderIndex || !!fieldForm.fieldIsUniqueDocumentIndex}
                          onChange={onFieldFormChange}
                          title='Determines if this field is part of a primary key. Documents with that key will be replaced during import'
                        />
                      </div>

                      <label style={{ fontWeight:500 }}>Order</label>
                      <input name='fieldControlOrderBy' type='number' inputMode='numeric' min={0} max={999} value={fieldForm.fieldControlOrderBy} onChange={onFieldFormChange} className="adm-input" style={{ maxWidth: 90 }} />

                      <label style={{ fontWeight:500 }}>Length</label>
                      <input name='fieldLength' type='number' inputMode='numeric' min={0} max={9999} value={fieldForm.fieldLength} onChange={onFieldFormChange} className="adm-input" style={{ maxWidth: 90 }} />
                    </div>
                                      {/*<label style={{ alignSelf:'center', fontWeight:500 }}>Show In Search</label>
                  <input type='checkbox' name='fieldShowInSearch' checked={fieldForm.fieldShowInSearch} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Show In Grid</label>
                  <input type='checkbox' name='fieldShowInDataGrid' checked={fieldForm.fieldShowInDataGrid} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Unique Folder</label>
                  <input type='checkbox' name='fieldIsUniqueFolderIndex' checked={fieldForm.fieldIsUniqueFolderIndex} onChange={onFieldFormChange} />
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Unique Document</label>
                  <input type='checkbox' name='fieldIsUniqueDocumentIndex' checked={fieldForm.fieldIsUniqueDocumentIndex} onChange={onFieldFormChange} />*/}
                  </div>

                  {/* Lookup configuration — full width */}
                  <div style={{ gridColumn:'1 / span 2', borderTop:'1px solid #eef2f6', paddingTop:12, marginTop:4 }}>
                    <div className="adm-section-title">Lookup configuratie</div>
                    <div style={{ display:'grid', gridTemplateColumns:'150px 1fr 150px 1fr', gap:10, alignItems:'center' }}>
                      <label style={{ fontWeight:500 }}>Lookup</label>
                      <select
                        value={fieldForm.fieldLookupName}
                        onChange={e => {
                          const val = e.target.value;
                          setFieldForm(p => ({ ...p, fieldLookupName: val, fieldLookupKeyColumn: '', fieldLookupDisplayColumn: '' }));
                        }}
                        className="adm-input"
                        style={{ maxWidth: 240 }}
                      >
                        <option value="">(geen)</option>
                        {availableLookups.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>

                      {fieldForm.fieldLookupName ? (
                        <>
                          <label style={{ fontWeight:500 }}>Key kolom</label>
                          <select
                            value={fieldForm.fieldLookupKeyColumn}
                            onChange={e => setFieldForm(p => ({ ...p, fieldLookupKeyColumn: e.target.value }))}
                            className="adm-input"
                            style={{ maxWidth: 240 }}
                            disabled={loadingLookupCols}
                          >
                            <option value="">(selecteer)</option>
                            {lookupColumns.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>

                          <label style={{ fontWeight:500 }}>Weergave kolom</label>
                          <select
                            value={fieldForm.fieldLookupDisplayColumn}
                            onChange={e => setFieldForm(p => ({ ...p, fieldLookupDisplayColumn: e.target.value }))}
                            className="adm-input"
                            style={{ maxWidth: 240 }}
                            disabled={loadingLookupCols}
                          >
                            <option value="">(selecteer)</option>
                            {lookupColumns.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>

                          <div style={{ gridColumn:'1 / -1', marginTop:8 }}>
                            <div style={{ fontWeight:500, marginBottom:6 }}>Extra veld mappings</div>
                            <div className="adm-hint" style={{ marginBottom:8 }}>Vul automatisch andere velden in bij selectie. Kolomnaam (lookup) → veldnaam (formulier).</div>
                            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                              {fieldForm.fieldLookupMappings.map((m, i) => (
                                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:8 }}>
                                  <select value={m.lookupColumn} onChange={e => { const next=[...fieldForm.fieldLookupMappings]; next[i]={...next[i],lookupColumn:e.target.value}; setFieldForm(p=>({...p,fieldLookupMappings:next})); }} className="adm-input" disabled={loadingLookupCols}>
                                    <option value="">(kolom)</option>
                                    {lookupColumns.map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                  <select value={m.targetFieldName} onChange={e => { const next=[...fieldForm.fieldLookupMappings]; next[i]={...next[i],targetFieldName:e.target.value}; setFieldForm(p=>({...p,fieldLookupMappings:next})); }} className="adm-input">
                                    <option value="">(veld)</option>
                                    {fields.map((f:any) => { const n=f.fieldName||f.FieldName||''; return n ? <option key={n} value={n}>{n}</option> : null; })}
                                  </select>
                                  <button type="button" onClick={() => { const next=fieldForm.fieldLookupMappings.filter((_,idx)=>idx!==i); setFieldForm(p=>({...p,fieldLookupMappings:next})); }} className="adm-btn adm-btn-danger">×</button>
                                </div>
                              ))}
                              <div>
                                <button type="button" onClick={() => setFieldForm(p=>({...p,fieldLookupMappings:[...p.fieldLookupMappings,{lookupColumn:'',targetFieldName:''}]}))} className="adm-btn adm-btn-secondary">+ Mapping toevoegen</button>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={{ gridColumn:'2 / -1', color:'var(--sc-text-muted)', fontSize:13, gridRow:'1' }}>Geen lookup geconfigureerd</div>
                      )}
                    </div>
                  </div>

                  <div className="adm-actions adm-actions--spread" style={{ gridColumn:'1 / span 2', marginTop:4 }}>
                    <div className="adm-actions-group">
                      <button type='submit' className="adm-btn adm-btn-primary" disabled={loading || !isFieldNameValid}>Save Field</button>
                      <button type='button' className="adm-btn adm-btn-secondary" onClick={handleCancelFieldEdit} disabled={loading}>Cancel</button>
                    </div>
                    {selectedFieldId != null && (
                      <button type='button' onClick={handleDeleteField} className="adm-btn adm-btn-danger" disabled={loading}>Delete Field</button>
                    )}
                  </div>
                </form>
              ) : (
                <div style={{ marginTop:16, color:'var(--sc-text-subtle)' }}>Select a field to view details or click 'Add Field'.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="adm-placeholder">Select a field model or click the + icon to add a new field model.</div>
        )}
      </div>
    </div>
  );
}
